import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { SafetyRepTask, SafetyRepTeam } from '../../../api/safetyRepWork';
import {
  highRiskTag,
  nextText,
  teamPathFor,
  stageColor,
  stageText,
  stepOfText,
  stepSegments,
  teamLeaders,
} from '../safetyRepWorkView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from '../SafetyRep.module.css';

type Props = { teams: SafetyRepTeam[]; tasks: SafetyRepTask[] };

/** Where each team is in the nine steps, and what happens next */
export const TeamsCard = ({ teams, tasks }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby="vo-teams" className={layout.card} data-gap="sm">
      <div className={layout.cardHeading}>
        <Heading level={2} data-size="sm" id="vo-teams">
          {t('safetyRep.work.teams.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.subtle}>
          {t('safetyRep.work.teams.intro')}
        </Paragraph>
      </div>
      {teams.length === 0 ? (
        <Paragraph data-size="sm">{t('safetyRep.work.teams.none')}</Paragraph>
      ) : (
        <ul className={classes.list}>
          {teams.map((team) => (
            <li key={team.id} className={classes.team}>
              <span className={classes.teamTitle}>
                <span className={classes.teamName}>
                  {team.survey ? (
                    <Link asChild>
                      <RouterLink
                        to={teamPathFor(team, team.survey.id)}
                        aria-label={t('safetyRep.work.teams.open', {
                          team: team.name,
                        })}
                      >
                        {team.name}
                      </RouterLink>
                    </Link>
                  ) : (
                    <span className="ds-label">{team.name}</span>
                  )}
                  <span
                    className={`ds-paragraph ${layout.subtle}`}
                    data-size="xs"
                  >
                    {t('safetyRep.work.teams.leader', {
                      leaders: teamLeaders(team, t),
                      count: team.size,
                    })}
                  </span>
                </span>
                <span className={classes.teamTags}>
                  {highRiskTag(team, t) && (
                    <Tag data-size="sm" data-color="danger">
                      {highRiskTag(team, t)}
                    </Tag>
                  )}
                  <Tag data-size="sm" data-color={stageColor(team)}>
                    {stageText(team, t)}
                  </Tag>
                </span>
              </span>
              <span className={classes.segments} aria-hidden="true">
                {stepSegments(team).map((state, index) => (
                  <span
                    key={index}
                    className={classes.segment}
                    data-state={state}
                  />
                ))}
              </span>
              <span className="ds-sr-only">{stepOfText(team, t)}.</span>
              <Paragraph data-size="sm">
                {nextText(team, tasks, t, i18n.language)}
              </Paragraph>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
